import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {ProductIdentity} from '../../models/product.model';
import {SalesCpiAction} from './sales-cpi.actions';
import {SalesCpiService} from '../../services/sales-cpi.service';
import {catchError, map, switchMap, tap} from 'rxjs/operators';
import {OfferCpi, OfferStatus} from '../../models/offer.model';
import {FormState} from '../../models/form-state.model';
import {clearFormValidationErrors, setFormValidationErrors} from '../../helpers/form.helper';
import {EMPTY, throwError} from 'rxjs';
import {PolicyService} from '../../services/policy.service';
import {PolicyCpi} from '../../models/policy.model';
import {NavService} from '../../services/nav.service';
import {NavItemCodes} from '../../enums/nav-item-codes.enum';
import {Attachment} from '../../models/attachment.model';
import {FileService} from '../../services/file.service';
import {AsideService} from '../../services/aside.service';
import {CpiCancellationAsideComponent} from '../../modules/policy/pages/policy-cpi-view/components/cpi-cancellation-aside';
import {CpiTerminationAsideComponent} from '../../modules/policy/pages/policy-cpi-view/components/cpi-termination-aside';
import {CpiBeneficiaryChangeAsideComponent} from '../../modules/policy/pages/policy-cpi-view/components/cpi-beneficiary-change-aside';
import {CpiPersonalDataAsideComponent} from '../../modules/policy/pages/policy-cpi-view/components/cpi-personal-data-aside';
import {ModalService} from '../../services/modal.service';
import {TranslateService} from '@ngx-translate/core';
import {AvailableQuestionnaire} from '../../models/questionnaire.model';
import {SalesPgStateModel} from '../sales-pg/sales-pg.state';
import {CpiPolicyModalComponent} from '../../modules/sales-cpi/components/cpi-policy-modal';


export interface SalesCpiStateModel {
    form: FormState;
    productList: ProductIdentity[];
    offer: OfferCpi;
    policy: PolicyCpi;
    attachments: Attachment[];
    questionnaire: AvailableQuestionnaire;
    questionnaireForm: FormState;
}

const getDefaults = () => ({
    form: new FormState(),
    productList: [],
    offer: null,
    policy: null,
    attachments: [],
    questionnaire: null,
    questionnaireForm: new FormState(),
});

@State<SalesCpiStateModel>({
    name: SalesCpiState.STATE_NAME,
    defaults: getDefaults()
})
@Injectable()
export class SalesCpiState {

    static readonly STATE_NAME = 'salesCpi';

    constructor(
        private salesCpiService: SalesCpiService,
        private translateService: TranslateService,
        private modalService: ModalService,
        private policyService: PolicyService,
        private navService: NavService,
        private fileService: FileService,
        private asideService: AsideService,
    ) {
    }

    @Selector()
    static productsList(state: SalesCpiStateModel) {
        return state.productList;
    }

    @Selector()
    static offer(state: SalesCpiStateModel) {
        return state.offer;
    }

    @Selector()
    static policy(state: SalesCpiStateModel) {
        return state.policy;
    }

    @Selector()
    static attachments(state: SalesCpiStateModel) {
        return state.attachments;
    }

    @Selector()
    static formErrors(state: SalesCpiStateModel) {
        return state.form.validationErrors;
    }

    @Selector()
    static questionnaire(state: SalesCpiStateModel) {
        return state.questionnaire;
    }

    @Action(SalesCpiAction.GetOffer)
    getOffer(ctx: StateContext<SalesCpiStateModel>, action: SalesCpiAction.GetOffer) {
        return this.salesCpiService.getOffer(action.offerId)
            .pipe(map(offer => {
                if (offer?.status?.code === OfferStatus.DRAFT) {
                    ctx.patchState({offer});
                } else {
                    // redirect to sales start if status !== DRAFT
                    this.navService.navigateByCode(NavItemCodes.SALES_CPI_PRODUCT_PICK);
                }
            }));
    }

    @Action(SalesCpiAction.GetPolicy)
    getPolicy(ctx: StateContext<SalesCpiStateModel>, action: SalesCpiAction.GetPolicy) {
        return this.policyService.getPolicy(action.policyId)
            .pipe(map(policy => ctx.patchState({policy: policy as PolicyCpi})));
    }

    @Action(SalesCpiAction.GetAttachments)
    getAttachments(ctx: StateContext<SalesCpiStateModel>, action: SalesCpiAction.GetAttachments) {
        return this.policyService.getAttachments(action.policyId)
            .pipe(map(attachments => ctx.patchState({attachments})));
    }

    @Action(SalesCpiAction.AddAttachment)
    addAttachment(ctx: StateContext<SalesCpiStateModel>, action: SalesCpiAction.AddAttachment) {
        return this.policyService.createAttachment(action.policyId, action.file)
            .pipe(map(attachments => ctx.patchState({attachments})));
    }

    @Action(SalesCpiAction.PolicyPrintout)
    policyPrintout(ctx: StateContext<SalesCpiStateModel>, action: SalesCpiAction.PolicyPrintout) {
        return this.policyService.printout(action.policyId, action.single)
            .pipe(
                map(({token}) => this.fileService.getFileByToken(token)),
                switchMap(() => ctx.dispatch(new SalesCpiAction.GetAttachments(action.policyId)))
            );
    }

    @Action(SalesCpiAction.DuplicatePrintout)
    duplicatePrintout(ctx: StateContext<SalesCpiStateModel>, action: SalesCpiAction.DuplicatePrintout) {
        return this.policyService.duplicate(action.policyId)
            .pipe(
                map(({token}) => this.fileService.getFileByToken(token)),
                switchMap(() => ctx.dispatch(new SalesCpiAction.GetAttachments(action.policyId)))
            );
    }

    @Action(SalesCpiAction.GetProductList)
    getProductList(ctx: StateContext<SalesCpiStateModel>) {
        return this.salesCpiService.fetchProducts()
            .pipe(map(productList => {
                ctx.patchState({productList});
                return productList;
            }));
    }

    @Action(SalesCpiAction.CreateOffer)
    createOffer(ctx: StateContext<SalesCpiStateModel>, action: SalesCpiAction.CreateOffer) {
        return this.salesCpiService.createOffer(action.productId)
            .pipe(tap(offer => this.navService.navigateByCode(NavItemCodes.SALES_CPI_OFFER_QUESTIONNAIRE, {offerId: offer.id})));
    }

    @Action(SalesCpiAction.Simulate)
    simulateCpi(ctx: StateContext<SalesCpiStateModel>, {offerId, configuration}: SalesCpiAction.Simulate) {
        return this.salesCpiService.simulate(offerId, configuration)
            .pipe(
                tap(() => clearFormValidationErrors(ctx)),
                tap(offer => ctx.patchState({offer})),
                catchError((error) => {
                    ctx.dispatch(new SalesCpiAction.GetOffer(offerId));
                    return this.catchValidation(error, ctx);
                })
            );
    }

    @Action(SalesCpiAction.SelectVariant)
    selectVariant(ctx: StateContext<SalesCpiStateModel>, action: SalesCpiAction.SelectVariant) {
        return this.salesCpiService.selectVariant(action.offerId, action.variantCode)
            .pipe(
                map(offer => {
                    ctx.patchState({offer});
                    this.navService.navigateByCode(NavItemCodes.SALES_CPI_OFFER_DATA, {offerId: offer.id});
                }),
            );
    }

    @Action(SalesCpiAction.OpenCancellationAside)
    openCancellationAside() {
        this.asideService.open(CpiCancellationAsideComponent);
    }

    @Action(SalesCpiAction.OpenTerminationAside)
    openTerminationAside() {
        this.asideService.open(CpiTerminationAsideComponent);
    }

    @Action(SalesCpiAction.OpenBeneficiaryChangeAside)
    openBeneficiaryChangeAside() {
        this.asideService.open(CpiBeneficiaryChangeAsideComponent);
    }

    @Action(SalesCpiAction.OpenPersonalDataChangeAside)
    openPersonalDataChangeAside() {
        this.asideService.open(CpiPersonalDataAsideComponent);
    }

    @Action(SalesCpiAction.Issue)
    issue(ctx: StateContext<SalesCpiStateModel>, action: SalesCpiAction.Issue) {
        return this.salesCpiService.issue(action.offerId, action.configuration)
            .pipe(
                map(policy => {
                    ctx.patchState({policy});
                    ctx.dispatch(new SalesCpiAction.OpenPolicyModal());
                }),
                catchError((error) => this.catchValidation(error, ctx))
            );
    }

    @Action(SalesCpiAction.OpenPolicyModal)
    openPolicyModal(ctx: StateContext<SalesCpiStateModel>, action: SalesCpiAction.OpenPolicyModal) {
        const policy = ctx.getState().policy;
        this.modalService.generic({
            nzContent: CpiPolicyModalComponent,
            nzFooter: [
                {
                    label: this.translateService.instant('SALES_CPI.POLICY.OPERATION.POLICY_PRINTOUT'),
                    type: 'default',
                    onClick: () => ctx.dispatch(new SalesCpiAction.PolicyPrintout(policy.id)),
                },
                {
                    label: this.translateService.instant('LABELS.CLOSE'),
                    type: 'primary',
                    onClick: (component: CpiPolicyModalComponent) => {
                        component.close();
                        this.navService.navigateByCode(NavItemCodes.POLICY_CPI, {policyId: policy.id});
                    },
                }
            ],
            nzKeyboard: false,
            nzClosable: false,
            nzMaskClosable: false,
            nzComponentParams: {policy},
        });
    }

    @Action(SalesCpiAction.FormFailure)
    addUserFailure(ctx: StateContext<SalesCpiStateModel>, {validationErrors = []}: SalesCpiAction.FormFailure) {
        setFormValidationErrors(ctx, validationErrors, SalesCpiState.STATE_NAME);
    }

    @Action(SalesCpiAction.GetQuestionnaire)
    getQuestionnaire(ctx: StateContext<SalesPgStateModel>) {
        const offerId = ctx.getState()?.offer?.id;
        if (!offerId) {
            return EMPTY;
        }
        return this.salesCpiService.getQuestionnaire(offerId).pipe(
            tap((questionnaire: AvailableQuestionnaire) => {
                if (questionnaire?.questions?.length > 0) {
                    ctx.patchState({
                        questionnaire,
                        questionnaireForm: new FormState(),
                    });
                } else {
                    ctx.patchState({
                        questionnaire: null,
                        questionnaireForm: new FormState(),
                    });
                }
            }),
            catchError((errorResponse) => {
                if (errorResponse?.status === 404) {
                    ctx.patchState({
                        questionnaire: null,
                        questionnaireForm: new FormState(),
                    });
                }
                return EMPTY;
            })
        );
    }

    @Action(SalesCpiAction.ValidateQuestionnaire)
    validateQuestionnaire(ctx: StateContext<SalesPgStateModel>, {payload}: SalesCpiAction.ValidateQuestionnaire) {
        const offerId = ctx.getState()?.offer?.id;
        return this.salesCpiService.validateQuestionnaire(offerId, payload).pipe(
            map((offer) => {
                ctx.patchState({offer});
                clearFormValidationErrors(ctx, [], 'questionnaireForm');
                this.navService.navigateByCode(NavItemCodes.SALES_CPI_OFFER_SIMULATE, {offerId});
            }),
            catchError(({error}) => {
                setFormValidationErrors(ctx, error, SalesCpiState.STATE_NAME, 'questionnaireForm');
                return EMPTY;
            })
        );
    }

    private catchValidation(error: any, ctx: StateContext<SalesCpiStateModel>) {
        if (error.status === 400) {
            ctx.dispatch(new SalesCpiAction.FormFailure(error.error));
        }
        return throwError(error);
    }

    @Action(SalesCpiAction.ClearSalesForm)
    clearState(ctx: StateContext<SalesCpiStateModel>) {
        const newState = getDefaults();
        delete newState.policy;
        delete newState.productList;
        delete newState.attachments;
        ctx.patchState(newState);
    }
}
